// 多租户模块
//
// 设计：
//   - 管理当前租户上下文（id/code/name/plan/features/theme/limits）
//   - 持久化到 localStorage，刷新页面不丢失
//   - 语言切换/租户切换通过事件通知订阅者（参照 tx.theme / tx.lang 模式）
//   - 暴露 getHeaders() 供 tx.ajax 统一注入请求头（X-Tenant-Id / X-Tenant-Code / Accept-Language）
//   - 支持一用户多租户场景：setList/getList 维护可选租户列表
//
// 挂载：window.ttx.tenant.{ get, set, clear, switch, on, off, getId, getHeaders, setList, getList, current }
//
// 用法：
//   ttx.tenant.set({ id: 't001', code: 'acme', name: 'ACME Corp' })
//   ttx.tenant.getId()                          // → 't001'
//   ttx.tenant.getHeaders()                     // → { 'X-Tenant-Id': 't001', 'X-Tenant-Code': 'acme' }
//   ttx.tenant.on('change', function (e) {...}) // 租户变化时触发
//   ttx.tenant.clear()                          // 退出登录时清空
window.ttx = window.ttx || {};
ttx.tenant = ttx.tenant || {};

(function () {
    var STORAGE_KEY = 'ttx-tenant';
    var LIST_KEY = 'ttx-tenant-list';

    // 当前租户信息：{ id, code, name, plan, features, theme, limits } 或 null
    var _tenant = null;

    // 可选租户列表（一用户多租户场景）
    var _list = [];

    // 事件监听器
    var _listeners = [];

    // ── 初始化：从 localStorage 读取 ─────────────────────────────────
    try {
        var saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            _tenant = JSON.parse(saved);
        }
        var savedList = localStorage.getItem(LIST_KEY);
        if (savedList) {
            _list = JSON.parse(savedList) || [];
        }
    } catch (e) {
        // localStorage 不可用或 JSON 解析失败时静默降级
    }

    // ── 核心 API ────────────────────────────────────────────────────

    /**
     * 获取当前租户信息
     * @returns {object|null} { id, code, name, plan, features, theme, limits } 或 null
     */
    ttx.tenant.get = function () {
        return _tenant;
    };

    /**
     * 设置当前租户并持久化，触发 'change' 事件
     * @param {object} info - 租户信息，至少包含 id
     *   - id {string} 租户ID
     *   - code {string} 租户编码
     *   - name {string} 租户名称
     *   - plan {string} 套餐，如 'free' / 'standard' / 'enterprise'
     *   - features {object} 功能开关，如 { exportExcel: true }
     *   - theme {object} 主题，如 { primaryColor: '#1456f0', logo: '/img/logo.png' }
     *   - limits {object} 套餐限制，如 { maxUsers: 50 }
     */
    ttx.tenant.set = function (info) {
        _tenant = info || null;

        try {
            if (_tenant) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(_tenant));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch (e) {
            // 持久化失败静默降级
        }

        _emit('change', { tenant: _tenant });
        try {
            window.dispatchEvent(new CustomEvent('tenantchange', { detail: { tenant: _tenant } }));
        } catch (e) {
            // CustomEvent 不可用时降级
        }
    };

    /**
     * 清空当前租户（退出登录时调用）
     */
    ttx.tenant.clear = function () {
        ttx.tenant.set(null);
    };

    /**
     * 切换租户
     * 当前实现：设置新租户 + 触发 change（由上层决定是否清缓存/重载）
     * 后续可扩展：自动清空租户相关缓存（tx.cache / tx.indexedDB）再 set
     * @param {object} info - 新租户信息
     */
    ttx.tenant.switch = function (info) {
        // 预留：在此处清空旧租户的缓存
        // ttx.cache && ttx.cache.clearByTenant && ttx.cache.clearByTenant();
        ttx.tenant.set(info);
    };

    /**
     * 快捷获取当前租户ID（供 tx.ajax 注入请求头）
     * @returns {string|null}
     */
    ttx.tenant.getId = function () {
        return _tenant ? _tenant.id : null;
    };

    /**
     * 返回租户相关请求头，供 tx.ajax 统一注入
     * 包含：X-Tenant-Id / X-Tenant-Code / Accept-Language（若有 lang 模块）
     * @returns {object} headers 对象，无租户时返回空对象（或仅含语言）
     */
    ttx.tenant.getHeaders = function () {
        var headers = {};

        if (_tenant) {
            if (_tenant.id) headers['X-Tenant-Id'] = _tenant.id;
            if (_tenant.code) headers['X-Tenant-Code'] = _tenant.code;
        }

        // 若 i18n 模块可用，顺带带上语言（一处注入，全局生效）
        if (ttx.lang && typeof ttx.lang.getLang === 'function') {
            headers['Accept-Language'] = ttx.lang.getLang();
        }

        return headers;
    };

    // ── 租户白名单 / 数据中心 / OSS 地址（先写死，后续改为可配置） ────
    // 内部写死值（占位，后续对接真实数据源时替换为从租户信息/接口获取）
    var _WHITELIST = [];
    var _DATA_CENTER = '';
    var _OSS_URL = '';

    /**
     * 获取租户白名单
     * 先写死，后续改为从租户配置/接口获取
     * @returns {Array}
     */
    ttx.tenant.getWhitelist = function () {
        return _WHITELIST;
    };

    /**
     * 获取租户数据中心
     * 先写死，后续改为从租户配置/接口获取
     * @returns {string}
     */
    ttx.tenant.getDataCenter = function () {
        return _DATA_CENTER;
    };

    /**
     * 获取租户 OSS 地址
     * 先写死，后续改为从租户配置/接口获取
     * @returns {string}
     */
    ttx.tenant.getOssUrl = function () {
        return _OSS_URL;
    };

    // ── 多租户列表（一用户多租户场景） ───────────────────────────────

    /**
     * 设置可选租户列表（登录后若返回多租户，存储供选择页使用）
     * @param {Array} list - [{ id, code, name }, ...]
     */
    ttx.tenant.setList = function (list) {
        _list = Array.isArray(list) ? list : [];

        try {
            localStorage.setItem(LIST_KEY, JSON.stringify(_list));
        } catch (e) {
            // 持久化失败静默降级
        }
    };

    /**
     * 获取可选租户列表
     * @returns {Array}
     */
    ttx.tenant.getList = function () {
        return _list;
    };

    // ── 事件订阅 ────────────────────────────────────────────────────

    /**
     * 订阅事件
     * @param {string} event - 事件名，目前支持 'change'
     * @param {function} cb - 回调，接收 { tenant } payload
     */
    ttx.tenant.on = function (event, cb) {
        if (typeof cb !== 'function') return;
        _listeners.push({ event: event, cb: cb });
    };

    /**
     * 取消订阅
     */
    ttx.tenant.off = function (event, cb) {
        _listeners = _listeners.filter(function (l) {
            return !(l.event === event && l.cb === cb);
        });
    };

    // ── 内部工具 ─────────────────────────────────────────────────────

    function _emit(event, payload) {
        _listeners.forEach(function (l) {
            if (l.event === event) {
                try {
                    l.cb(payload);
                } catch (e) {
                    // 单个监听器异常不影响其他
                    if (typeof console !== 'undefined' && console.warn) {
                        console.warn('[ttx.tenant] listener error:', e);
                    }
                }
            }
        });
    }

    // 暴露当前租户的只读引用（动态 getter 保证取到最新值）
    Object.defineProperty(ttx.tenant, 'current', {
        get: function () { return _tenant; },
        configurable: false,
        enumerable: true
    });
})();
